import {
	DcrFieldName,
	type DcrhcoInput,
	type Hco,
	ReferenceType
} from '@/core/graphql/generated/types';
import { getLocalSpecialtyValue } from '@/utils/specialty/getLocalSpecialtyValue';

import {
	LOCAL_SPECIALTY_MAX_ITEMS,
	NON_PRIMARY_LOCAL_SPECIALTY_IDS
} from './hcp.fields';
import {
	inlineCollection,
	inlineField,
	inlineName,
	inlineStatus,
	PROFILE_STATUS_FIELD_KEY
} from './inline';
import { labelKey as t } from './labelKey';
import type { FieldDef, HistoricFieldDef, InlineLayout } from './types';

export const HCO_FIELDS = {
	hcoNameCda_V: {
		label: t('Name'),
		section: 'name',
		kind: 'text',
		visibility: 'always',
		required: true,
		enumNames: [DcrFieldName.HcoNameCdaV]
	},
	internationalName_V: {
		label: t('International Name'),
		section: 'name',
		kind: 'text',
		visibility: 'off',
		enumNames: [DcrFieldName.InternationalNameV],
		backendNames: ['internationalNameCda_V']
	},

	primaryCountry_V: {
		label: t('Country'),
		section: 'organizational',
		kind: 'picklist',
		picklist: 'country',
		visibility: 'off',
		enumNames: [DcrFieldName.PrimaryCountryV]
	},
	hcoTypeCda_VId: {
		label: t('Global HCO Type'),
		section: 'organizational',
		kind: 'picklist',
		picklist: ReferenceType.HcoType,
		visibility: 'always',
		required: true,
		enumNames: [DcrFieldName.HcoTypeCdaVId],
		backendNames: ['hcoType']
	},
	hcoTypeLocal_VId: {
		label: t('Local HCO Type'),
		section: 'organizational',
		kind: 'picklist',
		picklist: ReferenceType.LocalHcoType,
		visibility: 'addable',
		enumNames: [DcrFieldName.HcoTypeLocalVId],
		backendNames: ['localHcoType']
	},
	academicStatus_VId: {
		label: t('Academic Status'),
		section: 'organizational',
		kind: 'picklist',
		picklist: ReferenceType.AcademicStatus,
		visibility: 'addable',
		countries: ['us'],
		hideWhenNoOptions: true,
		enumNames: [DcrFieldName.AcademicStatusVId],
		backendNames: ['academicStatus']
	},
	hcoOwnership_VId: {
		label: t('Ownership'),
		section: 'organizational',
		kind: 'picklist',
		picklist: ReferenceType.HcoOwnership,
		visibility: 'addable',
		enumNames: [DcrFieldName.HcoOwnershipVId],
		backendNames: ['ownership']
	},
	businessType_VId: {
		label: t('Ownership'),
		section: 'organizational',
		kind: 'picklist',
		picklist: ReferenceType.HcoBusinessType,
		visibility: 'off',
		enumNames: [DcrFieldName.BusinessTypeVId]
	},
	allSpecLocal_VId: {
		label: t('Local Specialties'),
		section: 'organizational',
		kind: 'multiPicklist',
		picklist: ReferenceType.SpecialtyLocal,
		visibility: 'addable',
		maxItems: LOCAL_SPECIALTY_MAX_ITEMS,
		primaryExcludedOptionIds: NON_PRIMARY_LOCAL_SPECIALTY_IDS,
		enumNames: [
			DcrFieldName.AllSpecLocalVId,
			DcrFieldName.Spec_1LocalVId,
			DcrFieldName.Spec_2LocalVId
		],
		backendNames: ['localSpecialties']
	},
	statusCda_VId: {
		label: t('Status'),
		section: 'organizational',
		kind: 'picklist',
		picklist: ReferenceType.Status,
		visibility: 'addable',
		enumNames: [DcrFieldName.StatusCdaVId]
	},
	countHcp_VId: {
		label: t('Number of Total Affiliated HCPs'),
		section: 'organizational',
		kind: 'picklist',
		picklist: ReferenceType.CountRange,
		visibility: 'off',
		enumNames: [DcrFieldName.CountHcpVId],
		backendNames: ['hcpCount']
	},
	countPrescriber_VId: {
		label: t('Number of Affiliated Prescribers'),
		section: 'organizational',
		kind: 'picklist',
		picklist: ReferenceType.CountRange,
		visibility: 'off',
		enumNames: [DcrFieldName.CountPrescriberVId],
		backendNames: ['prescriberCount']
	},

	nhidCda_V: {
		label: t('National Healthcare ID'),
		section: 'ids',
		kind: 'text',
		visibility: 'addable',
		enumNames: [DcrFieldName.NhidCdaV]
	},
	hcoTaxId_V: {
		label: t('Tax ID'),
		section: 'ids',
		kind: 'text',
		visibility: 'addable',
		enumNames: [DcrFieldName.HcoTaxIdV]
	},

	officePhoneCda_V: {
		label: t('Office Phone'),
		section: 'contact',
		kind: 'phone',
		visibility: 'off',
		enumNames: [DcrFieldName.OfficePhoneCdaV]
	},
	faxCda_V: {
		label: t('Fax'),
		section: 'contact',
		kind: 'phone',
		visibility: 'off',
		enumNames: [DcrFieldName.FaxCdaV]
	},
	emailCda_V: {
		label: t('Email'),
		section: 'contact',
		kind: 'email',
		visibility: 'addable',
		enumNames: [DcrFieldName.EmailCdaV]
	},
	websiteCda_V: {
		label: t('Website'),
		section: 'contact',
		kind: 'url',
		visibility: 'addable',
		enumNames: [DcrFieldName.WebsiteCdaV, DcrFieldName.Url_1V],
		backendNames: ['URL1_V', 'website_V']
	},

	typeOfHierarchy_VId: {
		label: t('Type of Hierarchy'),
		section: 'hierarchy',
		kind: 'picklist',
		picklist: ReferenceType.HcoHierarchyType,
		visibility: 'off',
		enumNames: [DcrFieldName.TypeOfHierarchyVId],
		backendNames: ['hcoHierarchyType']
	},

	parentHcos_V: {
		label: t('Parent HCOs'),
		section: 'affiliations',
		kind: 'collection',
		collection: 'parentHco',
		visibility: 'always',
		enumNames: [DcrFieldName.ParentHcosV],
		backendNames: ['parenthcos']
	},
	addresses_V: {
		label: t('Addresses'),
		section: 'addresses',
		kind: 'collection',
		collection: 'address',
		visibility: 'always',
		enumNames: [DcrFieldName.AddressesV],
		backendNames: ['addresses']
	},
	licenses_V: {
		label: t('Licenses'),
		section: 'licenses',
		kind: 'collection',
		collection: 'license',
		visibility: 'off',
		enumNames: [DcrFieldName.LicensesV],
		backendNames: ['licenses']
	}
} as const satisfies Partial<Record<keyof DcrhcoInput, FieldDef>>;

export const HCO_UNSUPPORTED_INPUT_KEYS = [
	'vid_V',
	'spec1Local_VId',
	'spec2Local_VId'
] as const satisfies readonly (keyof DcrhcoInput)[];

export const HCO_HISTORIC_FIELDS: readonly HistoricFieldDef[] = [
	{
		names: ['spec1Local_V', 'specialty1_V'],
		label: t('Primary Local Specialty'),
		anchor: 'allSpecLocal_VId'
	},
	{
		names: ['spec2Local_V', 'specialty2_V'],
		label: t('Local Specialty {{n}}', { n: 2 }),
		anchor: 'allSpecLocal_VId'
	},
	...[3, 4, 5, 6, 7, 8, 9, 10].map((number): HistoricFieldDef => ({
		names: [`specialty${number}_V`],
		label: t('Local Specialty {{n}}', { n: number }),
		anchor: 'allSpecLocal_VId'
	})),
	{
		names: ['phoneCda_V'],
		label: t('Phone'),
		anchor: 'officePhoneCda_V',
		kind: 'phone'
	},
	{
		names: ['majorClassOfTrade_V'],
		label: t('Major Class of Trade'),
		section: 'organizational'
	},
	{
		names: ['vaDodAffiliated_V'],
		label: t('Veterans Affairs - Department of Defense'),
		section: 'organizational'
	}
];

const getParentAffiliationCount = (profile: Hco) => {
	const search = (
		profile as Hco & { parentAffiliationSearch?: Hco['affiliationSearch'] }
	).parentAffiliationSearch;

	return search?.__typename === 'HCOAffiliationsResults'
		? search.paginationMetadata.total + (search.inactiveCount ?? 0)
		: 0;
};

export const HCO_INLINE_LAYOUT: InlineLayout<Hco, keyof typeof HCO_FIELDS> = {
	sections: [
		{ section: 'name', entries: [inlineName(['hcoNameCda_V'])] },
		{
			section: 'organizational',
			entries: [
				inlineStatus(),
				inlineField('hcoTypeCda_VId', p => p.hcoType?.name),
				inlineField('hcoTypeLocal_VId', p => p.localHcoType?.name),
				inlineField('academicStatus_VId', p => p.academicStatus?.name),
				inlineField('hcoOwnership_VId', p => p.ownership?.name),
				inlineField('allSpecLocal_VId', getLocalSpecialtyValue, {
					required: true
				})
			]
		},
		{
			section: 'ids',
			entries: [
				inlineField('nhidCda_V', p => p.nhidCda_V),
				inlineField('hcoTaxId_V', p => p.hcoTaxId_V)
			]
		},
		{
			section: 'contact',
			entries: [
				inlineField('officePhoneCda_V', p => p.officePhoneCda_V),
				inlineField('faxCda_V', p => p.faxCda_V),
				inlineField('emailCda_V', p => p.emailCda_V),
				inlineField('websiteCda_V', p => p.websiteCda_V)
			]
		},
		{
			section: 'affiliations',
			entries: [
				inlineCollection(
					'parentHcos_V',
					getParentAffiliationCount,
					t('Parent HCO')
				)
			]
		},
		{
			section: 'addresses',
			entries: [inlineCollection('addresses_V', p => p.addresses?.length ?? 0)]
		},
		{
			section: 'licenses',
			entries: [inlineCollection('licenses_V', p => p.licenses?.length ?? 0)]
		}
	],
	suggested: [
		PROFILE_STATUS_FIELD_KEY,
		'websiteCda_V',
		'hcoNameCda_V',
		'hcoTypeLocal_VId'
	]
};
